<script setup>
import { useI18n } from '../../i18n.js'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import Pts2EntityBadge from './Pts2EntityBadge.vue'

const { locale, t } = useI18n()

const props = defineProps({ detail: { type: Object, default: null }, loading: Boolean, error: { type: String, default: '' } })
const emit = defineEmits(['close'])
const panel = ref(null)
let previousFocus

function close() { emit('close') }
function keydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); close(); return }
  if (event.key !== 'Tab') return
  const items = [...(panel.value?.querySelectorAll('button,[href],[tabindex]:not([tabindex="-1"])') ?? [])]
    .filter((item) => !item.disabled)
  if (!items.length) return
  const [first] = items, last = items.at(-1)
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}

function number(value, digits = 3) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : String(value)
}
function date(value, timeZone) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (!Number.isFinite(parsed.getTime())) return String(value)
  const format = { dateStyle: 'short', timeStyle: 'medium' }
  if (timeZone) format.timeZone = timeZone
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(parsed)
}
function tagValue(tag) {
  if (tag?.state === 'ABSENT') return t('pts2UiHistoryNoTag')
  if (tag?.state === 'UNAVAILABLE') return tag.maskedValue ? t('pts2UiHistoryMaskedUnavailable', { value: tag.maskedValue }) : t('pts2UiHistoryUnavailable')
  return tag?.value || tag?.maskedValue || '—'
}
function tagOwner(tag) {
  if (tag?.name) return tag.name
  if (tag?.state === 'ABSENT') return t('pts2UiHistoryNotApplied')
  if (tag?.state === 'UNAVAILABLE') return t('pts2UiHistoryUnavailableData')
  return t('pts2UiHistoryOwnerUnspecified')
}
function userLogin(user) {
  if (user?.login) return user.login
  return user?.id == null ? t('pts2UiHistoryNotProvided') : t('pts2UiHistoryNameNotProvided')
}

onMounted(async () => {
  previousFocus = document.activeElement
  document.body.classList.add('drawer-open')
  await nextTick(); panel.value?.querySelector('.pts2-drawer-close')?.focus()
})
onBeforeUnmount(() => { document.body.classList.remove('drawer-open'); previousFocus?.focus?.() })
</script>

<template>
  <div class="pts2-transaction-drawer-layer" @keydown="keydown">
    <button class="pts2-transaction-backdrop" type="button" :aria-label="t('pts2UiHistoryCloseDetails')" @click="close"></button>
    <aside ref="panel" class="pts2-transaction-drawer" role="dialog" aria-modal="true" aria-labelledby="pts2-transaction-title">
      <header><div><small>{{ t('pts2UiHistoryTransactionDetails') }}</small><h2 id="pts2-transaction-title">{{ t('pts2UiHistoryTransactionNumber', { number: detail?.transaction?.transactionNumber ?? '—' }) }}</h2></div>
        <button class="pts2-drawer-close" type="button" :aria-label="t('pts2UiHistoryCloseDetails')" @click="close"><XMarkIcon aria-hidden="true" /></button>
      </header>
      <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2UiHistoryLoadingDetails') }}</p>
      <p v-else-if="error" class="notice error" role="alert">{{ error }}</p>
      <div v-else-if="detail" class="pts2-transaction-detail">
        <section class="pts2-transaction-summary">
          <span>{{ t('pts2UiHistoryAmount') }}</span><strong>{{ number(detail.transaction.amount, 2) }}</strong>
          <div><p><span>{{ t('pts2UiHistoryVolume') }}</span><b>{{ number(detail.transaction.volume) }} {{ t('pts2UiHistoryLiters') }}</b></p>
            <p><span>{{ t('pts2UiHistoryPrice') }}</span><b>{{ number(detail.transaction.price, 2) }} {{ t('pts2UiHistoryCurrencyPerLiter') }}</b></p>
            <p><span>{{ t('pts2UiHistoryFuel') }}</span><Pts2EntityBadge :entity-id="detail.transaction.fuelGradeId" :label="detail.transaction.fuelGradeName || '—'" /></p></div>
        </section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryFuelDispensing') }}</h3><dl>
          <div><dt>{{ t('pts2UiHistoryPumpNumberLabel') }}</dt><dd>{{ detail.transaction.pumpNumber ?? '—' }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryNozzleNumberLabel') }}</dt><dd>{{ detail.transaction.nozzleNumber ?? '—' }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryTankNumberLabel') }}</dt><dd>{{ detail.transaction.tankNumber ?? '—' }}</dd></div>
          <div><dt>{{ t('pts2UiHistoryShiftNumberLabel') }}</dt><dd>{{ detail.transaction.shiftNumber ?? t('pts2UiHistoryNoShift') }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryCompensatedLiters') }}<small>{{ t('pts2UiHistoryCompensatedTC') }}</small></dt><dd>{{ number(detail.tcVolume) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryTagLabel') }}</dt><dd :class="{ 'metadata-state': !detail.tag.name }">{{ tagOwner(detail.tag) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryTagId') }}</dt><dd class="private-value">{{ tagValue(detail.tag) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryAccount') }}</dt><dd :class="{ 'metadata-state': !detail.user.login }">{{ userLogin(detail.user) }}</dd></div>
          <div class="wide"><dt>UserId</dt><dd>{{ detail.user.id ?? '—' }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryTotals') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryTotalVolumeLiters') }}<small>TotalVolume</small></dt><dd>{{ number(detail.totalVolume) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryTotalAmount') }}<small>TotalAmount</small></dt><dd>{{ number(detail.totalAmount) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryTransactionTime') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryStart') }}</dt><dd>{{ date(detail.dateTimeStart, detail.transaction.stationTimeZone) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryEnd') }}</dt><dd>{{ date(detail.transaction.eventAt, detail.transaction.stationTimeZone) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryReceived') }}</dt><dd>{{ date(detail.transaction.receivedAt, detail.transaction.stationTimeZone) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryProvenance') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryStation') }}</dt><dd>{{ detail.stationName || '—' }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryController') }}</dt><dd>{{ detail.controllerName || '—' }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryTransport') }}</dt><dd>{{ detail.transport || '—' }}</dd></div>
          <div class="wide"><dt>ConfigurationId</dt><dd>{{ detail.configurationId || '—' }}</dd></div>
        </dl></section>
      </div>
      <footer><button class="pc-secondary" type="button" @click="close">{{ t('pts2UiHistoryClose') }}</button></footer>
    </aside>
  </div>
</template>
